import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { meAPI } from '../services/api';
import { DEFAULT_PRESET, resolvePreset } from './dateRanges';
import { terms as accountTerms } from '../design/terms';

export const FILTER_STORAGE_KEY = 'vl-filter';

const EMPTY_FILTER = { nodeId: null, communityIds: [], preset: DEFAULT_PRESET };

const readStoredFilter = () => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(FILTER_STORAGE_KEY) || 'null');
    if (!stored) return EMPTY_FILTER;
    return {
      nodeId: typeof stored.nodeId === 'string' ? stored.nodeId : null,
      communityIds: Array.isArray(stored.communityIds) ? stored.communityIds.filter((id) => typeof id === 'string') : [],
      preset: typeof stored.preset === 'string' ? stored.preset : DEFAULT_PRESET,
    };
  } catch (e) {
    return EMPTY_FILTER;
  }
};

const collectCommunityIds = (node) =>
  (node.communityIds || []).concat(...(node.children || []).map((child) => collectCommunityIds(child)));

const collectNodeIds = (nodes) => nodes.flatMap((n) => [n.id, ...collectNodeIds(n.children || [])]);

/** The parts of GET me/filter-options the filter bar uses: levels in order, the node tree and the communities. */
export function readFilterOptions(options) {
  return {
    levels: Array.isArray(options?.levels) ? [...options.levels].sort((a, b) => a.order - b.order) : [],
    tree: Array.isArray(options?.tree) ? options.tree : [],
    communities: Array.isArray(options?.communities) ? options.communities : [],
  };
}

/** Drops a stored selection the user can no longer see (scope changed since it was saved). */
export function pruneFilter(filter, { tree, communities }) {
  const nodeIds = new Set(collectNodeIds(tree));
  const communityIds = new Set(communities.map((c) => c.id));
  const nodeId = filter.nodeId && nodeIds.has(filter.nodeId) ? filter.nodeId : null;
  const kept = filter.communityIds.filter((id) => communityIds.has(id));
  if (nodeId === filter.nodeId && kept.length === filter.communityIds.length) return filter;
  return { ...filter, nodeId, communityIds: kept };
}

/** Query parameters for AnalyticsFilter (contracts.md §7): hierarchyNodeIds, communityIds, from, to. */
export function filterToParams(filter, range) {
  const params = { from: range.from, to: range.to };
  if (filter.communityIds.length) params.communityIds = filter.communityIds.join(',');
  else if (filter.nodeId) params.hierarchyNodeIds = filter.nodeId;
  return params;
}

const HierarchyFilterContext = createContext(null);

/**
 * Global hierarchy filter: one top-level node (Leland: a Division) or a set of communities, plus a
 * date preset (calendar days in the account time zone). Persisted for the browser session, like the mockups. Levels, nodes and communities come
 * from GET me/filter-options, already limited to what the signed-in user may see, so the filter bar
 * reads in the customer's own words and works for roles without ViewAccountSettings (board members).
 */
export function HierarchyFilterProvider({ orgName, timeZone, terms = accountTerms(), children }) {
  const [filter, setFilterState] = useState(readStoredFilter);
  const [levels, setLevels] = useState([]);
  const [tree, setTree] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [status, setStatus] = useState('loading');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    meAPI
      .getFilterOptions()
      .then((options) => {
        if (cancelled) return;
        const read = readFilterOptions(options);
        setLevels(read.levels);
        setTree(read.tree);
        setCommunities(read.communities);
        setFilterState((current) => pruneFilter(current, read));
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const setFilter = useCallback((patch) => {
    setFilterState((current) => {
      const next = { ...current, ...patch };
      try {
        window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        // Storage blocked: the filter lasts for this page view only.
      }
      return next;
    });
  }, []);

  const value = useMemo(() => {
    const topLevel = levels[0];
    const topLevelName = topLevel?.displayName || 'Division';
    const topLevelPlural = topLevel?.pluralDisplayName || `${topLevelName}s`;
    const communitiesById = Object.fromEntries(communities.map((c) => [c.id, c]));
    const nodesById = {};
    const walk = (nodes) =>
      nodes.forEach((n) => {
        nodesById[n.id] = n;
        walk(n.children || []);
      });
    walk(tree);
    const topNodes = tree.map((node) => ({ ...node, allCommunityIds: collectCommunityIds(node) }));
    const selectedNode = filter.nodeId ? nodesById[filter.nodeId] : null;
    const range = resolvePreset(filter.preset, new Date(), timeZone);
    const orgLabel = orgName || 'your organization';

    let label = `All of ${orgLabel}`;
    if (filter.communityIds.length === 1) label = communitiesById[filter.communityIds[0]]?.name || terms.count(1);
    else if (filter.communityIds.length > 1) label = terms.count(filter.communityIds.length);
    else if (selectedNode) label = `${selectedNode.name} ${topLevelName.toLowerCase()}`;

    return {
      status,
      levels,
      topLevelName,
      topLevelPlural,
      communityLabel: terms.community,
      terms,
      tree,
      topNodes,
      communities,
      communitiesById,
      nodesById,
      filter,
      selectedNode,
      range,
      label,
      params: filterToParams(filter, range),
      setDivision: (nodeId) => setFilter({ nodeId: nodeId || null, communityIds: [] }),
      setCommunities: (ids) => setFilter({ communityIds: ids }),
      setPreset: (preset) => setFilter({ preset }),
      clear: () => setFilter({ nodeId: null, communityIds: [] }),
      reload: () => setReloadKey((k) => k + 1),
    };
  }, [status, levels, tree, communities, filter, orgName, timeZone, terms, setFilter]);

  return React.createElement(HierarchyFilterContext.Provider, { value }, children);
}

export function useHierarchyFilter() {
  const context = useContext(HierarchyFilterContext);
  if (!context) throw new Error('useHierarchyFilter must be used within HierarchyFilterProvider');
  return context;
}

export default useHierarchyFilter;
